import { openAlertDetails } from "./appNavigation";

const ALERT_CODE_RE = /^[0-9A-Za-z]{5}$/;

export function normalizeAlertCode(code) {
  return (code || "").trim().toUpperCase();
}

export function isValidAlertCode(code) {
  return ALERT_CODE_RE.test(normalizeAlertCode(code));
}

export function pickTicketIdFromAlert(alert) {
  // formato do by-code: objeto last_ticket com { id }
  if (alert?.last_ticket?.id) return alert.last_ticket.id;

  // formato antigo: array tickets com { ticket_id }
  const tickets = alert?.tickets;
  if (Array.isArray(tickets) && tickets.length > 0) {
    const lastTicket = tickets[tickets.length - 1];
    return lastTicket?.ticket_id || tickets[0]?.ticket_id || null;
  }

  return null;
}

/** Compatível com o shape usado em AlertDetails (report_by_area / by-code). */
export function mapAlertByCodeToDetailsShape(apiAlert) {
  if (!apiAlert) return null;

  return {
    alert_id: apiAlert.alert_id,
    cod_alerta: apiAlert.cod_alerta,
    area_title: apiAlert.area,
    area_id_code: apiAlert.area_id_code,
    alert_type_description: apiAlert.alert_type,
    severity_level_description: apiAlert.severity,
    coordinates: apiAlert.coordinates,
    alert_created_at: apiAlert.created_at,
    alert_observation: apiAlert.observation,
    respondido: apiAlert.respondido,
    last_action: apiAlert.last_action,
    last_action_label: apiAlert.last_action_label,
    tickets: (apiAlert.tickets || []).map((ticket) => ({
      ticket_id: ticket.ticket_id,
      alert_id: apiAlert.alert_id,
      action: ticket.action,
      responses: ticket.responses,
      observations: ticket.observations,
      ticket_created_at: ticket.created_at,
      created_by: ticket.created_by,
      photos: ticket.photos || [],
      attachments: ticket.attachments || [],
      sent_team: ticket.sent_team || [],
      other_responsibles: ticket.other_responsibles || [],
    })),
    area_responsibles: apiAlert.area_responsibles || null,
  };
}

export function getAlertSearchErrorMessage(error) {
  const status = error?.response?.status;
  const apiError = error?.response?.data?.error;

  if (status === 401) {
    return "Sessão expirada. Faça login novamente.";
  }
  if (status === 404) {
    return "Nenhum alerta encontrado com este código.";
  }
  if (status === 400) {
    return apiError || "Informe o código do alerta.";
  }
  return apiError || "Erro ao buscar alerta. Tente novamente.";
}

export function getAlertRequestSearchErrorMessage(error) {
  const status = error?.response?.status;
  const apiError = error?.response?.data?.error;

  if (status === 401) {
    return "Sessão expirada. Faça login novamente.";
  }
  if (status === 404) {
    return "Código não encontrado. Confira o e-mail ou a lista.";
  }
  if (status === 400) {
    return apiError || "Informe o código da solicitação.";
  }
  return apiError || "Erro ao buscar solicitação. Tente novamente.";
}

/**
 * Abre detalhes do alerta pelo código (mesmo fluxo de AlertCodeSearchBar).
 * @param {Function} getAlertByCode - guardianAlertService.getAlertByCode
 */
export async function openAlertByCode(dispatch, navigate, rawCode, getAlertByCode) {
  const code = normalizeAlertCode(rawCode);
  if (!isValidAlertCode(code)) {
    throw new Error("Informe um código de 5 caracteres alfanuméricos.");
  }

  const data = await getAlertByCode(code);
  if (!data?.success || !data?.alert) {
    throw new Error("Nenhum alerta encontrado com este código.");
  }

  const alert = data.alert;
  const ticketId = pickTicketIdFromAlert(alert);
  const navigationState = { codAlerta: alert.cod_alerta };

  if (ticketId) {
    openAlertDetails(dispatch, navigate, { ...navigationState, ticketId });
    return alert;
  }

  openAlertDetails(dispatch, navigate, {
    ...navigationState,
    preloadedAlert: mapAlertByCodeToDetailsShape(alert),
  });
  return alert;
}
